import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { bootstrapApplication as bootstrapApplication_1, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { NavigationActionTiming, routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { AbstractSecurityStorage, DefaultLocalStorageService, provideAuth } from 'angular-auth-oidc-client';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import p from '../package.json';

import { AdminMaintenancePeriodApi } from './app/admin/components/maintenance-period/services/admin-maintenance-period-api.service';
import { AdminMaintenancePeriodService } from './app/admin/components/maintenance-period/services/admin-maintenance-period.service';
import adminMaintenancePeriodReducer, {
    ADMIN_MAINTENANCE_PERIOD_INITIAL_STATE,
} from './app/admin/components/maintenance-period/store/reducers';
import { AdminAuthGuard } from './app/admin/guards/auth.guard';
import { RoleGuardService } from './app/admin/guards/role.guard';
import { AdminAuthService } from './app/admin/services/auth.service';
import { BackOfficeService } from './app/admin/services/back.office.service';
import { CountriesService } from './app/admin/services/countries.service';
import { AdminHttpService } from './app/admin/services/http.service';
import { AdminSecurityService } from './app/admin/services/security.service';
import { AdminSharedService } from './app/admin/services/shared.service';
import { AdminApi } from './app/admin/store/api/admin';
import adminReducer, { ADMIN_INITIAL_STATE } from './app/admin/store/reducers/admin';
import { ALERT_RESOLUTION_GATEWAY } from './app/alert-resolution/gateways/AlertResolutionGateway';
import { HttpAlertResolutionGateway } from './app/alert-resolution/gateways/HttpAlertResolutionGateway';
import { HttpAlertMailingConfigGateway } from './app/alerts-notification-config/gateways/HttpAlertMailingConfigGateway';
import { HttpAlertNotificationConfigGateway } from './app/alerts-notification-config/gateways/HttpAlertNotificationConfigGateway';
import { AlertsNotificationConfigService } from './app/alerts-notification-config/services/alerts-notification-config.service';
import { AlertsMailingConfigEffects } from './app/alerts-notification-config/store/effects/alerts-mailing-config';
import { AlertsNotificationConfigEffects } from './app/alerts-notification-config/store/effects/alerts-notification-config';
import { AlertNotificationAnalyticsEffects } from './app/alerts-notification-config/store/effects/analytics';
import alertsNotificationReducer, {
    ALERTS_NOTIFICATION_INITIAL_STATE,
} from './app/alerts-notification-config/store/reducers/alerts-notification-config';
import { AppComponent } from './app/app.component';
import { routing } from './app/app.routing';
import { AppConfig } from './app/AppConfig';
import { getAuthConfig } from './app/auth/auth.config';
import { UserService } from './app/core/services/user.service';
import { LangEffects } from './app/core/store/effects/lang';
import { BobHttpClient } from './app/enduser/gateways/BobHttpClient';
import { HttpLegalDocumentGateway as EndUserHttpLegalDocumentGateway } from './app/enduser/gateways/HttpLegalDocumentGateway';
import { HttpLinkRequestsGateway as EndUserHttpLinkRequestsGateway } from './app/enduser/gateways/HttpLinkRequestsGateway';
import { AcceptLinkRequestUseCase } from './app/enduser/services/AcceptLinkRequestUseCase';
import { LinkRequestUiStore } from './app/enduser/services/LinkRequestUiStore';
import { RejectLinkRequestUseCase } from './app/enduser/services/RejectLinkRequestUseCase';
import { InterventionEffects } from './app/interventions/store/effects/intervention.effects';
import interventionsReducer, { INTERVENTIONS_INITIAL_STATE } from './app/interventions/store/reducers/interventions';
import { HttpLegalDocumentGateway } from './app/legal-documents/services/HttpLegalDocumentGateway';
import { LegalDocumentService } from './app/legal-documents/services/LegalDocument.service';
import { LegalDocumentsEffects } from './app/legal-documents/store/effects/legalDocuments';
import legalDocumentsReducer, {
    LEGAL_DOCUMENTS_INITIAL_STATE,
} from './app/legal-documents/store/reducers/legalDocument';
import { HttpLinkRequestsGateway } from './app/link-requests/services/HttpLinkRequestsGateway';
import { LinkRequestsService } from './app/link-requests/store/api/link-requests.service';
import { LinkRequestEffects } from './app/link-requests/store/effects/link-requests';
import linkRequestsReducer, { LINK_REQUESTS_INITIAL_STATE } from './app/link-requests/store/reducers/link-requests';
import { MaintenancePeriodService } from './app/maintenance-period/services/maintenancePeriod.service';
import { MaintenancePeriodsEffects } from './app/maintenance-period/store/effects/maintenancePeriods';
import maintenancePeriodReducer, {
    MAINTENANCE_PERIOD_INITIAL_STATE,
} from './app/maintenance-period/store/reducers/maintenancePeriod';
import { MessagesEffects } from './app/messages/effects/messages';
import { MessageService } from './app/messages/services/message.service';
import { MessagesApi } from './app/messages/store/api/messages';
import messageReducer, { MESSAGES_INITIAL_STATE } from './app/messages/store/reducers/messages';
import { provideFeatureFlags } from './app/shared/feature-flags/feature-flags.providers';
import { AnalyticsGuard } from './app/shared/guards/analytics.guard';
import { AuthGuard } from './app/shared/guards/auth.guard';
import { createTranslateLoader } from './app/shared/i18n/createTranslateLoader';
import { APP_CONFIG, WEBSOCKET, WINDOW_OBJECT } from './app/shared/injectionTokens';
import { TokenInterceptor } from './app/shared/interceptors/TokenInterceptor';
import { AnalyticsService } from './app/shared/services/analytics.service';
import { BobHttpService } from './app/shared/services/bob-http.service';
import { createUiSocket } from './app/shared/services/createUiSocket';
import { DevicesAPI } from './app/shared/services/DevicesAPI';
import { ErrorService } from './app/shared/services/error.service';
import { HttpService } from './app/shared/services/http.service';
import { LocalStorageService } from './app/shared/services/localStorage.service';
import { ModalService } from './app/shared/services/modal.service';
import { SecurityService } from './app/shared/services/SecurityService';
import { socketActionHandlersToken } from './app/shared/services/SocketActionHandler';
import { DeviceCreatedHandler } from './app/shared/services/socketActionHandlers/DeviceCreatedHandler';
import { DeviceLabelUpdatedHandler } from './app/shared/services/socketActionHandlers/DeviceLabelUpdatedHandler';
import { DeviceRemovedHandler } from './app/shared/services/socketActionHandlers/DeviceRemovedHandler';
import { DeviceStatesUpdatedHandler } from './app/shared/services/socketActionHandlers/DeviceStatesUpdatedHandler';
import { ExecutionStateChangedHandler } from './app/shared/services/socketActionHandlers/ExecutionStateChangedHandler';
import { InterventionGrantedHandler } from './app/shared/services/socketActionHandlers/InterventionGrantedHandler';
import { NewInstallerMessageHandler } from './app/shared/services/socketActionHandlers/NewInstallerMessageHandler';
import { ServegoAccessGrantedHandler } from './app/shared/services/socketActionHandlers/ServegoAccessGrantedHandler';
import { VideoCallHungUpHandler } from './app/shared/services/socketActionHandlers/VideoCallHungUpHandler';
import { VideoCallInvitationSentHandler } from './app/shared/services/socketActionHandlers/VideoCallInvitationSentHandler';
import { VideoCallJoinedParticipantHandler } from './app/shared/services/socketActionHandlers/VideoCallJoinedParticipantHandler';
import { VideoCallLeftParticipantHandler } from './app/shared/services/socketActionHandlers/VideoCallLeftParticipantHandler';
import { UISocketService } from './app/shared/services/ui.socket.service';
import { SharedModule } from './app/shared/shared.module';
import { UserEnvApi } from './app/shared/store/api/user-env';
import { AuthEffects } from './app/shared/store/effects/auth';
import loginReducer, { LOGIN_INITIAL_STATE } from './app/shared/store/reducers/login';
import userEnvReducer, { USER_ENV_INITIAL_STATE } from './app/shared/store/reducers/user-env';
import { DEVICE_GATEWAY } from './app/site-detail/services/DeviceGateway';
import { HttpDeviceGateway } from './app/site-detail/services/HttpDeviceGateway';
import { HttpSetupGateway } from './app/site-detail/services/HttpSetupGateway';
import { HttpSiteGateway } from './app/site-detail/services/HttpSiteGateway';
import { SETUP_GATEWAY } from './app/site-detail/services/SetupGateway';
import { SITE_GATEWAY } from './app/site-detail/services/SiteGateway';
import { SiteDetailAnalyticsEffects } from './app/site-detail/store/effects/analytics';
import { SiteDetailBoxEffects } from './app/site-detail/store/effects/box';
import { DeviceDetailEffects } from './app/site-detail/store/effects/device-detail';
import { SiteDetailDeviceUpdateEffects } from './app/site-detail/store/effects/device-update';
import { SiteDetailNavigationEffects } from './app/site-detail/store/effects/navigation';
import { NotificationsEffects } from './app/site-detail/store/effects/notifications';
import { SiteDetailEffects } from './app/site-detail/store/effects/site-detail';
import { SiteDetailSocketsEffects } from './app/site-detail/store/effects/sockets';
import { SiteDetailTasksEffects } from './app/site-detail/store/effects/tasks';
import deviceDetailReducer, { DEVICE_DETAIL_INITIAL_STATE } from './app/site-detail/store/reducers/device-detail';
import devicesReducer, { DEVICES_INITIAL_STATE } from './app/site-detail/store/reducers/devices';
import gatewayReducer, { GATEWAYS_INITIAL_STATE } from './app/site-detail/store/reducers/gateways';
import uiReducer, { UI_INITIAL_STATE } from './app/site-detail/store/reducers/ui';
import { FavoriteErrorHandler } from './app/site-favorite/services/FavoriteErrorHandler';
import { HttpFavoritesGateway } from './app/site-favorite/services/HttpFavoritesGateway';
import { FavoriteSiteService } from './app/site-favorite/store/api/favorite-site.service';
import { FavoriteSitesEffects } from './app/site-favorite/store/effects/favorite-site';
import favoriteSitesReducer, { FAVORITE_SITES_INITIAL_STATE } from './app/site-favorite/store/reducers/favorite-site';
import { HttpStatsService } from './app/site-list/services/http-stats.service';
import SitesReducer, { SITES_INITIAL_STATE } from './app/site-list/store/reducers/sites';
import { HttpToolboxGateway } from './app/toolbox/services/http-toolbox-gateway.service';
import { ToolboxEffects } from './app/toolbox/store/effects/toolbox';
import toolboxReducer, { TOOLBOX_INITIAL_STATE } from './app/toolbox/store/reducers/toolbox';
import { DailyVideoCallProvider } from './app/video-conference/services/DailyVideoCallProvider';
import { HttpVideoCallGateway } from './app/video-conference/services/HttpVideoCallGateway';
import { VIDEO_CALL_GATEWAY } from './app/video-conference/services/VideoCallGateway';
import { VideoCallParticipantManager } from './app/video-conference/services/VideoCallParticipantManager';
import { VIDEO_CALL_PROVIDER } from './app/video-conference/services/VideoCallProvider';
import { VideoCallService } from './app/video-conference/services/VideoCallService';
import { UIVideoCallEffects } from './app/video-conference/store/effects/ui-video-call';
import { VideoCallEffects } from './app/video-conference/store/effects/video-call';
import uiVideoCallsReducer, { UI_VIDEO_CALLS_INITIAL_STATE } from './app/video-conference/store/reducers/ui-video-call';
import videoCallsReducer, { VIDEO_CALLS_INITIAL_STATE } from './app/video-conference/store/reducers/video-call';
import { detectBrowser } from './detectBrowser';
import { environment } from './environments/environment';
import { initializeTagCommander } from './initializeTagCommander';

function bootstrapApplication() {
    fetch(`/config/${environment.configFile}?v=${p.version}`)
        .then((response) => response.json())
        .then((config: AppConfig) => {
            detectBrowser();

            if (environment.production) {
                initializeTagCommander(config);
                enableProdMode();
            }

            bootstrapApplication_1(AppComponent, {
                providers: [
                    {
                        provide: OVERLAY_DEFAULT_CONFIG,
                        useValue: { usePopover: false },
                    },
                    provideZoneChangeDetection(),
                    importProvidersFrom(
                        BrowserModule,
                        FormsModule,
                        ReactiveFormsModule,
                        MatDialogModule,
                        MatExpansionModule,
                        MatGridListModule,
                        routing,
                        EffectsModule.forRoot([]),
                        EffectsModule.forFeature([
                            AlertNotificationAnalyticsEffects,
                            AlertsNotificationConfigEffects,
                            AlertsMailingConfigEffects,
                            AuthEffects,
                            FavoriteSitesEffects,
                            InterventionEffects,
                            NotificationsEffects,
                            LangEffects,
                            MaintenancePeriodsEffects,
                            MessagesEffects,
                            LegalDocumentsEffects,
                            LinkRequestEffects,
                            SiteDetailAnalyticsEffects,
                            SiteDetailBoxEffects,
                            DeviceDetailEffects,
                            SiteDetailDeviceUpdateEffects,
                            SiteDetailTasksEffects,
                            SiteDetailEffects,
                            SiteDetailNavigationEffects,
                            SiteDetailSocketsEffects,
                            ToolboxEffects,
                            VideoCallEffects,
                            UIVideoCallEffects,
                        ]),
                        StoreModule.forRoot({
                            router: routerReducer,
                        }),
                        StoreDevtoolsModule.instrument({
                            maxAge: 25,
                            trace: true,
                            traceLimit: 40,
                            logOnly: environment.production,
                        }),
                        StoreModule.forFeature('user-env', userEnvReducer, {
                            initialState: USER_ENV_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('ui-state', uiReducer, {
                            initialState: UI_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('interventions', interventionsReducer, {
                            initialState: INTERVENTIONS_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('toolbox', toolboxReducer, {
                            initialState: TOOLBOX_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('site-favorite', favoriteSitesReducer, {
                            initialState: FAVORITE_SITES_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('sites', SitesReducer, {
                            initialState: SITES_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('link-requests', linkRequestsReducer, {
                            initialState: LINK_REQUESTS_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('login', loginReducer, {
                            initialState: LOGIN_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('legal-documents', legalDocumentsReducer, {
                            initialState: LEGAL_DOCUMENTS_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('admin', adminReducer, {
                            initialState: ADMIN_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('messages', messageReducer, {
                            initialState: MESSAGES_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('devices', devicesReducer, {
                            initialState: DEVICES_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('device-detail', deviceDetailReducer, {
                            initialState: DEVICE_DETAIL_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('admin-maintenance-periods', adminMaintenancePeriodReducer, {
                            initialState: ADMIN_MAINTENANCE_PERIOD_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('maintenance-period', maintenancePeriodReducer, {
                            initialState: MAINTENANCE_PERIOD_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('gateways', gatewayReducer, {
                            initialState: GATEWAYS_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('alerts-notification', alertsNotificationReducer, {
                            initialState: ALERTS_NOTIFICATION_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('video-calls', videoCallsReducer, {
                            initialState: VIDEO_CALLS_INITIAL_STATE,
                        }),
                        StoreModule.forFeature('ui-video-calls', uiVideoCallsReducer, {
                            initialState: UI_VIDEO_CALLS_INITIAL_STATE,
                        }),
                        SharedModule,
                        MatBadgeModule,
                        MatSelectModule,
                        NgxMaskDirective,
                        ScrollingModule,
                        MatTooltipModule,
                        MatDialogModule,
                        StoreRouterConnectingModule.forRoot({
                            navigationActionTiming: NavigationActionTiming.PostActivation,
                        }),
                    ),
                    {
                        provide: HTTP_INTERCEPTORS,
                        useClass: TokenInterceptor,
                        multi: true,
                    },
                    provideHttpClient(withInterceptorsFromDi()),
                    provideTranslateService({
                        loader: { provide: TranslateLoader, useFactory: createTranslateLoader },
                    }),
                    provideAuth(getAuthConfig(config)),
                    {
                        provide: AbstractSecurityStorage,
                        useClass: DefaultLocalStorageService,
                    },
                    provideAnimations(),
                    provideCharts(withDefaultRegisterables()),
                    provideNgxMask(),
                    { provide: APP_CONFIG, useValue: config },
                    provideFeatureFlags(),
                    AnalyticsService,
                    HttpFavoritesGateway,
                    HttpToolboxGateway,
                    HttpStatsService,
                    LegalDocumentService,
                    UserService,
                    AlertsNotificationConfigService,
                    MessageService,
                    ModalService,
                    UISocketService,
                    AuthGuard,
                    AnalyticsGuard,
                    AdminAuthGuard,
                    RoleGuardService,
                    CountriesService,
                    HttpService,
                    BobHttpService,
                    BobHttpClient,
                    AdminAuthService,
                    AdminHttpService,
                    BackOfficeService,
                    AdminMaintenancePeriodService,
                    AdminSecurityService,
                    AdminSharedService,
                    UserEnvApi,
                    LinkRequestsService,
                    LinkRequestUiStore,
                    AcceptLinkRequestUseCase,
                    RejectLinkRequestUseCase,
                    SecurityService,
                    FavoriteSiteService,
                    HttpLinkRequestsGateway,
                    EndUserHttpLegalDocumentGateway,
                    EndUserHttpLinkRequestsGateway,
                    FavoriteErrorHandler,
                    AdminApi,
                    AdminMaintenancePeriodApi,
                    LocalStorageService,
                    VideoCallService,
                    MessagesApi,
                    MaintenancePeriodService,
                    {
                        provide: WINDOW_OBJECT,
                        useValue: window,
                    },
                    {
                        provide: WEBSOCKET,
                        useFactory: (config: AppConfig) => createUiSocket(config.servegoApi.url),
                        deps: [APP_CONFIG],
                    },
                    ErrorService,
                    DevicesAPI,
                    DailyVideoCallProvider,
                    VideoCallParticipantManager,
                    { provide: ALERT_RESOLUTION_GATEWAY, useClass: HttpAlertResolutionGateway },
                    { provide: 'FavoriteSitesGateway', useClass: HttpFavoritesGateway },
                    { provide: SITE_GATEWAY, useExisting: HttpSiteGateway },
                    { provide: DEVICE_GATEWAY, useExisting: HttpDeviceGateway },
                    { provide: 'LegalDocumentGateway', useClass: HttpLegalDocumentGateway },
                    { provide: 'LinkRequestsGateway', useClass: EndUserHttpLinkRequestsGateway },
                    { provide: 'EndUserLegalDocumentGateway', useClass: EndUserHttpLegalDocumentGateway },
                    { provide: SETUP_GATEWAY, useExisting: HttpSetupGateway },
                    { provide: 'AlertNotificationConfigGateway', useClass: HttpAlertNotificationConfigGateway },
                    { provide: 'AlertMailingConfigGateway', useClass: HttpAlertMailingConfigGateway },
                    { provide: VIDEO_CALL_GATEWAY, useClass: HttpVideoCallGateway },
                    { provide: VIDEO_CALL_PROVIDER, useClass: DailyVideoCallProvider },
                    { provide: 'VideoCallProvider', useExisting: VIDEO_CALL_PROVIDER },
                    { provide: 'ValueStorage', useClass: LocalStorageService },
                    { provide: socketActionHandlersToken, useClass: DeviceCreatedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: DeviceLabelUpdatedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: DeviceRemovedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: DeviceStatesUpdatedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: ExecutionStateChangedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: InterventionGrantedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: NewInstallerMessageHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: ServegoAccessGrantedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallHungUpHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallInvitationSentHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallJoinedParticipantHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallLeftParticipantHandler, multi: true },
                ],
            }).catch((err) => console.error(err));
        });
}

bootstrapApplication();
